import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { RightsProfilesApiFactory } from '../api/rights-profiles.api';
import type { RightsProfile } from '../types/rights-profile.types';

interface UseRightsProfileResult {
  profile: RightsProfile | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Fetches a single rights profile by id (for the edit page). Mirrors
 * `useGetInstanceById`: aborts in-flight requests on id change / unmount.
 */
export function useRightsProfile(id: string | undefined): UseRightsProfileResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => RightsProfilesApiFactory(client), [client]);

  const [profile, setProfile] = useState<RightsProfile | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!id) return;

    const ctrl = new AbortController();
    setProfile(null);
    setError(null);

    api
      .getById(id, { signal: ctrl.signal })
      .then((loaded) => setProfile(loaded))
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setProfile(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api, id]);

  return { profile, loading, error };
}
